import type { AssetManifestKey } from "../../assets/manifest";
import { FEATURE_FLAGS } from "../../config/features";
import { crateCorePatterns, crateShellSkins } from "../crates/catalog";
import type { ProfileProgress } from "../progression/levels";
import type { OrbitForgeSave } from "../save/types";

export type CosmeticKind = "shellSkin" | "corePattern";
export type CosmeticSource = "level" | "crate" | "prestige";

export interface CosmeticDefinition {
  readonly kind: CosmeticKind;
  readonly id: string;
  readonly label: string;
  readonly source: CosmeticSource;
  readonly unlockLevel: number | null;
  readonly assetKey: AssetManifestKey | null;
}

const crateCoreAssetKeys: Record<string, AssetManifestKey> = {
  dice_core: "cosmeticCoreDiceCore",
  prism_core: "cosmeticCorePrismCore",
  checker_core: "cosmeticCoreCheckerCore"
};

export const levelShellSkinDefinitions: readonly CosmeticDefinition[] = [
  { kind: "shellSkin", id: "white_arc", label: "White Arc", source: "level", unlockLevel: 1, assetKey: null },
  { kind: "shellSkin", id: "cyan_pulse", label: "Cyan Pulse", source: "level", unlockLevel: 3, assetKey: null },
  { kind: "shellSkin", id: "lime_flux", label: "Lime Flux", source: "level", unlockLevel: 6, assetKey: null },
  { kind: "shellSkin", id: "pink_circuit", label: "Pink Circuit", source: "level", unlockLevel: 10, assetKey: null },
  { kind: "shellSkin", id: "gold_relay", label: "Gold Relay", source: "level", unlockLevel: 15, assetKey: null },
  { kind: "shellSkin", id: "violet_nova", label: "Violet Nova", source: "level", unlockLevel: 22, assetKey: null }
];

export const levelCorePatternDefinitions: readonly CosmeticDefinition[] = [
  { kind: "corePattern", id: "clean_core", label: "Clean Core", source: "level", unlockLevel: 1, assetKey: null },
  { kind: "corePattern", id: "hollow_core", label: "Hollow Core", source: "level", unlockLevel: 4, assetKey: null },
  { kind: "corePattern", id: "target_core", label: "Target Core", source: "level", unlockLevel: 8, assetKey: null },
  { kind: "corePattern", id: "split_core", label: "Split Core", source: "level", unlockLevel: 13, assetKey: null },
  { kind: "corePattern", id: "spark_core", label: "Spark Core", source: "level", unlockLevel: 16, assetKey: null },
  { kind: "corePattern", id: "twin_core", label: "Twin Core", source: "level", unlockLevel: 25, assetKey: null }
];

export const prestigeCorePatternDefinitions: readonly CosmeticDefinition[] = FEATURE_FLAGS.prestige
  ? [{ kind: "corePattern", id: "crown_core", label: "Crown Core", source: "prestige", unlockLevel: null, assetKey: null }]
  : [];

export const crateShellSkinDefinitions: readonly CosmeticDefinition[] = crateShellSkins.map((skin) => ({
  kind: "shellSkin",
  id: skin.id,
  label: skin.label,
  source: "crate",
  unlockLevel: null,
  assetKey: null
}));

export const crateCorePatternDefinitions: readonly CosmeticDefinition[] = crateCorePatterns.map((pattern) => ({
  kind: "corePattern",
  id: pattern.id,
  label: pattern.label,
  source: "crate",
  unlockLevel: null,
  assetKey: crateCoreAssetKeys[pattern.id] ?? null
}));

export const shellSkinDefinitions: readonly CosmeticDefinition[] = [
  ...levelShellSkinDefinitions,
  ...crateShellSkinDefinitions
];

export const corePatternDefinitions: readonly CosmeticDefinition[] = [
  ...levelCorePatternDefinitions,
  ...crateCorePatternDefinitions,
  ...prestigeCorePatternDefinitions
];

export function cosmeticDefinitionsFor(kind: CosmeticKind): readonly CosmeticDefinition[] {
  return kind === "shellSkin" ? shellSkinDefinitions : corePatternDefinitions;
}

export function findCosmeticDefinition(kind: CosmeticKind, id: string): CosmeticDefinition | null {
  return cosmeticDefinitionsFor(kind).find((definition) => definition.id === id) ?? null;
}

export function cosmeticFeedbackTarget(kind: CosmeticKind, id: string): string {
  return `cosmetic-${kind}-${id}`;
}

export function isCosmeticUnlocked(definition: CosmeticDefinition, save: OrbitForgeSave, progress: ProfileProgress): boolean {
  if (definition.source === "level") {
    return progress.effectiveLevel >= (definition.unlockLevel ?? 1);
  }

  if (definition.source === "prestige") {
    return FEATURE_FLAGS.prestige && progress.prestigeLevel > 0;
  }

  return definition.kind === "shellSkin"
    ? save.cosmetics.UnlockedCrateShellSkins.includes(definition.id)
    : save.cosmetics.UnlockedCrateCorePatterns.includes(definition.id);
}
